import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  Stat,
  useToast,
} from "../../design-system";
import EmployeeMultiPicker from "../../Components/Kronaly/Empleados/EmployeeMultiPicker";
import {
  enqueueReport,
  getEmployees,
  getHoursReport,
} from "../../helpers/kronaly_helper";

/**
 * Vista de reporte "Horas trabajadas + Horas extra".
 *
 * Componente de contenido para el hub /reportes. La authorization, el
 * PageHeader y el routing los maneja el hub padre; aquí solo viven los
 * filtros, la tabla y el export CSV.
 *
 * El CSV se genera client-side a partir del payload — el endpoint
 * devuelve la lista plana, así que no hace falta una segunda llamada.
 */
export function HoursReport({ isManager }) {
  const toast = useToast();

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const firstOfMonth = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  }, []);

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isManager) return;
    getEmployees().then((res) => {
      const list = res?.data || res || [];
      setEmployees(Array.isArray(list) ? list : []);
    }).catch(() => setEmployees([]));
  }, [isManager]);

  const run = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedIds.length > 0) params.employee_profile_ids = selectedIds;
      const res = await getHoursReport(params);
      setData(res?.data || res || null);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar el reporte.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedIds]);

  useEffect(() => {
    if (isManager) run();
    // eslint-disable-next-line
  }, [isManager]);

  /**
   * Encola un reporte para generación en background. El usuario recibirá
   * notificación + email cuando esté listo, y podrá descargarlo desde la
   * sección "Mis reportes" debajo.
   */
  const onEnqueue = async (format) => {
    if (!startDate || !endDate) {
      toast.error("Selecciona un rango de fechas primero.");
      return;
    }
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedIds.length > 0) params.employee_profile_ids = selectedIds;
      await enqueueReport({ type: "hours", format, params });
      toast.success(
        `Reporte ${format.toUpperCase()} en cola`,
        "Te avisaremos cuando esté listo. Lo verás en la sección 'Mis reportes' al final."
      );
    } catch (e) {
      toast.error("No se pudo encolar el reporte", e?.response?.data?.message || e?.message || "");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button
          variant="secondary"
          icon="FileText"
          onClick={() => onEnqueue("pdf")}
          disabled={!startDate || !endDate}
          title="Genera el reporte en PDF en segundo plano"
        >
          Generar PDF
        </Button>
        <Button
          variant="secondary"
          icon="Download"
          onClick={() => onEnqueue("csv")}
          disabled={!startDate || !endDate}
          title="Genera el reporte en CSV en segundo plano"
        >
          Generar CSV
        </Button>
      </div>

      <Card>
        <div className="kr-row-2" style={{ gridTemplateColumns: "1fr 1fr auto auto", alignItems: "end", gap: 12 }}>
          <Field label="Desde">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
            />
          </Field>
          <Field label="Hasta">
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
            />
          </Field>
          <Button variant="ghost" onClick={() => { setStartDate(firstOfMonth); setEndDate(today); setSelectedIds([]); }}>
            Reiniciar
          </Button>
          <Button variant="primary" icon="Play" onClick={run} disabled={loading}>
            {loading ? "Generando…" : "Generar"}
          </Button>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field label="Empleados" help="Vacío = todos los empleados activos del workspace.">
            <EmployeeMultiPicker
              employees={employees}
              value={selectedIds}
              onChange={setSelectedIds}
            />
          </Field>
        </div>
      </Card>

      {error && <Alert tone="danger">{error}</Alert>}

      {loading && !data && (
        <Card>
          <div style={{ padding: 32, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        </Card>
      )}

      {data && (
        <>
          <Card>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
              <Stat label="Empleados" value={data.summary?.employees ?? 0} />
              <Stat label="Planificado" value={fmtHours(data.summary?.planned_minutes)} />
              <Stat label="Trabajado" value={fmtHours(data.summary?.worked_minutes)} />
              <Stat
                label="Balance"
                value={fmtHoursSigned(data.summary?.balance_minutes)}
              />
              <Stat label="Horas extra aprobadas" value={fmtHours(data.summary?.overtime_approved_minutes)} />
            </div>
          </Card>

          <Card padded={false}>
            <div
              style={{
                padding: "14px 20px",
                borderBottom: "1px solid var(--kr-border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
                Detalle por empleado
              </div>
              <Badge tone="neutral">{data.rows?.length || 0} filas</Badge>
            </div>

            {(!data.rows || data.rows.length === 0) ? (
              <EmptyState
                icon="FileText"
                title="Sin datos"
                description="No hay jornadas registradas para el filtro seleccionado."
              />
            ) : (
              <ReportTable rows={data.rows} />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export default HoursReport;

function ReportTable({ rows }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
          padding: "10px 20px",
          background: "var(--kr-neutral-50)",
          borderBottom: "1px solid var(--kr-border)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          color: "var(--kr-fg-muted)",
          minWidth: 720,
        }}
      >
        <span>Empleado</span>
        <span>Planificado</span>
        <span>Trabajado</span>
        <span>Balance</span>
        <span>Extra trabajado</span>
        <span>Extra aprobado</span>
      </div>
      {rows.map((r, i) => (
        <div
          key={r.employee_profile_id}
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
            padding: "10px 20px",
            borderBottom: i === rows.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
            fontSize: 13,
            minWidth: 720,
          }}
        >
          <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>
            {r.employee_name || "—"}
            {r.position && (
              <div style={{ color: "var(--kr-fg-muted)", fontSize: 11, marginTop: 2 }}>
                {r.position}
              </div>
            )}
          </div>
          <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-muted)" }}>
            {fmtHours(r.planned_minutes)}
          </div>
          <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", fontWeight: 600 }}>
            {fmtHours(r.worked_minutes)}
          </div>
          <div
            style={{
              fontFamily: "var(--kr-font-mono)",
              color: r.balance_minutes < 0 ? "var(--kr-warning-700)" : "var(--kr-success-700)",
              fontWeight: 600,
            }}
          >
            {fmtHoursSigned(r.balance_minutes)}
          </div>
          <div
            style={{
              fontFamily: "var(--kr-font-mono)",
              color: r.overtime_worked_minutes > 0 ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
            }}
          >
            {fmtHours(r.overtime_worked_minutes)}
          </div>
          <div
            style={{
              fontFamily: "var(--kr-font-mono)",
              color: r.overtime_approved_minutes > 0 ? "var(--kr-success-700)" : "var(--kr-fg-muted)",
              fontWeight: 600,
            }}
          >
            {fmtHours(r.overtime_approved_minutes)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- helpers ----------

function fmtHours(minutes) {
  if (minutes === null || minutes === undefined) return "—";
  const m = Math.abs(Number(minutes) || 0);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h}h` : `${h}h ${String(mm).padStart(2, "0")}m`;
}

function fmtHoursSigned(minutes) {
  if (minutes === null || minutes === undefined) return "—";
  const n = Number(minutes) || 0;
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${fmtHours(Math.abs(n))}`;
}

function buildCsv(data) {
  const header = [
    "employee_profile_id",
    "employee_name",
    "position",
    "planned_minutes",
    "worked_minutes",
    "balance_minutes",
    "overtime_worked_minutes",
    "overtime_approved_minutes",
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes('"') || s.includes(",") || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [header.join(",")];
  for (const r of data.rows || []) {
    lines.push([
      escape(r.employee_profile_id),
      escape(r.employee_name),
      escape(r.position),
      escape(r.planned_minutes),
      escape(r.worked_minutes),
      escape(r.balance_minutes),
      escape(r.overtime_worked_minutes),
      escape(r.overtime_approved_minutes),
    ].join(","));
  }
  return lines.join("\n");
}
